import { Close, DeleteOutlineOutlined, DeleteOutlineRounded, EditOutlined, Search, VisibilityOutlined, WarningAmberRounded } from '@mui/icons-material'
import { Avatar, Box, Button, Chip, Dialog, DialogContent, IconButton, InputAdornment, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { ApiDeleteDelear, ApiGetDealerList } from '../../../Core/Apicall';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../../Redux/store';
import { Dealer } from '../../../types/Dealer';
import dayjs from 'dayjs';
import { UserRole } from '../../../types/UserRole';

function Dealers() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const navigate = useNavigate()
    const [dealerlist, setDealerList] = useState<Dealer[]>()
    const [total, setTotal] = useState<number>(-1)
    const { userDetails } = useSelector((state: RootState) => state.global)
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Dealer | null>(null);
    const userRole: UserRole = (userDetails?.role as UserRole) ?? "client";
    const [search, setSearch] = useState('')


    const filteredDealers = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return dealerlist ?? []
        return (dealerlist ?? []).filter((c) =>
            [
                c.companyName,
                c.email,
                c.contactPerson,
            ].some((v) => v?.toLowerCase().includes(term))
        )
    }, [dealerlist, search])

    const rows = [
        {
            initials: "TI",
            color: "#009688",
            client: "TechVista Solutions",
            owner: "John Doe",
            email: "john@techvista.com",
            plan: "Professional",
            status: "Active",
            dealers: 12,
            products: 245,
            joined: "May 14, 2026",
        },
        {
            initials: "GT",
            color: "#2E7D32",
            client: "Global Traders",
            owner: "Sarah Johnson",
            email: "sarah@globaltraders.com",
            plan: "Standard",
            status: "Active",
            dealers: 8,
            products: 156,
            joined: "May 13, 2026",
        },
        {
            initials: "NG",
            color: "#00897B",
            client: "NextGen Solutions",
            owner: "Michael Brown",
            email: "michael@nextgen.com",
            plan: "Enterprise",
            status: "Active",
            dealers: 20,
            products: 430,
            joined: "May 12, 2026",
        },
        {
            initials: "PS",
            color: "#EF6C00",
            client: "Premium Supplies",
            owner: "David Wilson",
            email: "david@premium.com",
            plan: "Professional",
            status: "Pending",
            dealers: 5,
            products: 89,
            joined: "May 12, 2026",
        },
        {
            initials: "ED",
            color: "#C62828",
            client: "Elite Distributors",
            owner: "Emma Davis",
            email: "emma@elite.com",
            plan: "Standard",
            status: "Suspended",
            dealers: 3,
            products: 67,
            joined: "May 11, 2026",
        },
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Active":
                return {
                    color: "#16A34A",
                    bgcolor: "#DCFCE7",
                };
            case "Pending":
                return {
                    color: "#D97706",
                    bgcolor: "#FEF3C7",
                };
            case "Suspended":
                return {
                    color: "#DC2626",
                    bgcolor: "#FEE2E2",
                };
            default:
                return {};
        }
    };

    const getDealerList = async () => {
        setDealerList([])
        try {
            const data = `?offset=${page * rowsPerPage}&limit=${rowsPerPage}&clientId=${userDetails?.clientId}`
            // const data = `?clientId=${userDetails?.clientId}`
            const res = await ApiGetDealerList(data)
            if (res?.status || res?.success) {
                setDealerList(res?.data)
                setTotal(res?.total)
            }

        } catch (error: any) {
            toast.error(error?.message || "Something went wrong")
        }
    }

    useEffect(() => {
        getDealerList()
    }, [page])


    const handleClose = () => {
        setSelectedClient(null)
        setOpenDelete(false)
    }


    const onDelete = async () => {
        try {
            const data = `?subClientId=${selectedClient?.subClientId}`
            const res = await ApiDeleteDelear(data)
            if (res?.status) {
                handleClose()
                getDealerList()
            }
        } catch (error: any) {
            toast.error(error?.message || "Something went wrong")
        }
    }

    return (
        <Box className='p-lg-3 p-xl-4 p-2'>
            <Box className='d-flex mb-4 gap-3 flex-column flex-md-row align-items-start align-items-md-center justify-content-between'>
                <Box>
                    <Typography variant='h5' className='fw-bold' sx={{ lineHeight: 'normal' }}>All Dealers</Typography>
                    <Typography variant='body2'>Manage all your platform clients</Typography>
                </Box>
                <Box className='d-flex align-items-center gap-2 text-field justify-content-md-end justify-content-between' sx={{ width: { xs: "100%", md: 'auto' } }}>
                    <TextField
                        className='m-0 p-0'
                        placeholder='Search Dealers...'
                        sx={{ minWidth: { xs: "auto", md: '300px' } }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start" className='me-0'>
                                        <Search fontSize="small" className='m-0 p-0' />
                                    </InputAdornment>
                                ),
                                endAdornment: search && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearch('')}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Button className='btn-main px-3 py-2' onClick={() => { navigate(`/${userRole}/add-or-update-dealer`) }}>+ Add Dealer</Button>
                </Box>
            </Box>
            {/* <Box className='card-bg border-10 border-main'> */}
            <Box>
                <TableContainer className='w-100 card-bg'
                    sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        // overflow: "hidden",
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                bgcolor: "var(--primary-light-color)",
                                "& th": {
                                    fontWeight: 600,
                                    color: "#555",
                                    py: 2,
                                    borderBottom: "1px solid #E5E7EB",
                                },
                            }}>
                                {[
                                    "Dealer",
                                    "Owner",
                                    "Email",
                                    // "PLAN",
                                    // "STATUS",
                                    // "PRODUCTS",
                                    "Joined",
                                    "Actions",
                                ].map((item) => (
                                    <TableCell key={item}>
                                        <Typography
                                            variant="caption"
                                        >
                                            {item}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredDealers?.map((row: Dealer) => (
                                <TableRow
                                    key={row.subClientId}
                                    hover
                                    sx={{
                                        "& td": {
                                            py: 2,
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Stack direction="row" spacing={2} className='align-items-center'>
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: "var(--primary-color)",
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {row?.subClientAdmin?.firstName[0]}{row?.subClientAdmin?.lastName[0]}
                                            </Avatar>

                                            <Typography >
                                                {row.companyName}
                                            </Typography>
                                        </Stack>
                                    </TableCell>

                                    <TableCell>{row.contactPerson}</TableCell>

                                    <TableCell>{row.email}</TableCell>

                                    {/* <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.plan}
                                            sx={{
                                                bgcolor: "#F5F5F5",
                                            }}
                                        />
                                    </TableCell> */}

                                    {/* <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.status}
                                            sx={{
                                                ...getStatusColor(row.status),
                                                fontWeight: 600,
                                            }}
                                        />
                                    </TableCell> */}


                                    {/* <TableCell>N/A</TableCell> */}

                                    <TableCell>{dayjs(row.createdAt).format("DD/MM/YYYY HH:MM")}</TableCell>

                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton size="small" onClick={() => { navigate(`/${userRole}/add-or-update-dealer/${row?.subClientId}`) }}>
                                                <EditOutlined className='text-main' fontSize="small" />
                                            </IconButton>

                                            <IconButton size="small" onClick={() => { setSelectedClient(row); setOpenDelete(true) }}>
                                                <DeleteOutlineOutlined className='text-red' fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box className='d-flex justify-content-end'>
                    {total > rowsPerPage && <Pagination
                        page={page + 1}
                        count={Math.ceil(total / rowsPerPage)}
                        onChange={(_, value) => setPage(value - 1)}
                        shape="rounded"
                        // color="primary"
                        sx={{
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: 'var(--primary-color)',
                                color: 'white'
                            }
                        }}
                        showFirstButton={false}
                        showLastButton={false}
                        siblingCount={1}
                        boundaryCount={1}
                    />}
                </Box>
            </Box>
            {/* </Box> */}
            <Dialog
                open={openDelete}
                onClose={() => { handleClose() }}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "10px",
                            p: 1,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <DialogContent sx={{ p: 4, position: "relative" }}>
                    <IconButton
                        onClick={() => { handleClose() }}
                        sx={{
                            position: "absolute",
                            right: 18,
                            top: 18,
                        }}
                    >
                        <Close />
                    </IconButton>


                    <Box className='d-flex align-items-center  justify-content-between mb-3'>
                        <Box
                            className='mx-auto'
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: "50%",
                                bgcolor: "#FDECEC",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                            }}
                        >
                            <DeleteOutlineRounded
                                className='text-red'
                                sx={{
                                    fontSize: 60,
                                }}
                            />

                            <WarningAmberRounded
                                className='text-red'
                                sx={{
                                    position: "absolute",
                                    bottom: 18,
                                    right: 14,
                                    bgcolor: "#fff",
                                    borderRadius: "50%",
                                }}
                            />
                        </Box>
                    </Box>

                    <Typography
                        variant="h5"
                        className='text-center fw-bold'
                        gutterBottom
                    >
                        Delete Dealer?
                    </Typography>

                    <Typography
                        className='text-grey text-center mb-4 mx-5'
                    >
                        Are you sure you want to delete this dealer?
                        This action cannot be undone.
                    </Typography>

                    <Box
                        className='p-3 border-main border-10 mb-3'>
                        <Stack
                            direction="row"
                            className='justify-content-between mb-2'
                        >
                            <Typography className='text-grey'>
                                Company Name
                            </Typography>

                            <Typography className='fw-bold' >
                                {selectedClient?.companyName}
                            </Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            className='justify-content-between mb-2'
                        >
                            <Typography className='text-grey'>
                                Company Email
                            </Typography>

                            <Typography className='fw-bold'>
                                {selectedClient?.email}
                            </Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            className='justify-content-between'
                        >
                            <Typography className='text-grey'>
                                Created On
                            </Typography>

                            <Typography className='fw-bold'>
                                {dayjs(selectedClient?.createdAt).format("DD MMM YYYY")}
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Warning */}

                    <Box
                        className='text-red'
                        sx={{
                            bgcolor: "#FFF3F3",
                            border: "1px solid var(--danger-color)",
                            borderRadius: 3,
                            p: 2,
                            display: "flex",
                            gap: 2,
                            mb: 4,
                        }}
                    >
                        <WarningAmberRounded />

                        <Typography variant="body2">
                            All dealer data including users,
                            products, orders and settings
                            will be permanently deleted.
                        </Typography>
                    </Box>

                    {/* Buttons */}

                    <Stack direction="row" spacing={2}>
                        <Button
                            className='btn-outlined-main'
                            fullWidth
                            variant="outlined"
                            onClick={() => { handleClose() }}
                            sx={{
                                height: 52,
                                borderRadius: 2,
                                textTransform: "none",
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<DeleteOutlineRounded />}
                            className='border-10'
                            onClick={onDelete}
                            sx={{
                                bgcolor: "var(--danger-color)",
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            Delete Dealer
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default Dealers
